import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

@Directive({
    selector: '[appPostalCodeFormat]',
    standalone: false
})
export class PostalCodeFormatDirective implements AfterViewInit, OnDestroy {
    @Input() postalCodeController: AbstractControl;

    private readonly _destroySubscriptions = new Subject();

    constructor(private readonly el: ElementRef<HTMLInputElement>) { }

    ngOnDestroy(): void {
        this._destroySubscriptions.next(undefined);
        this._destroySubscriptions.complete();
    }

    ngAfterViewInit(): void {
        this.postalCodeController.valueChanges
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                filter(value => value !== null && value !== undefined),
                takeUntil(this._destroySubscriptions)
            )
            .subscribe((value: string) => {
                const formatted = this.formatPostalCode(value);
                if (formatted !== value) {
                    this.postalCodeController.setValue(formatted, { emitEvent: false });
                }
            });
    }

    private updateAndEmitFormattedValue() {
        const input = this.el.nativeElement;
        const formatted = this.formatPostalCode(input.value);
        input.value = formatted;
        this.postalCodeController.setValue(formatted);
    }

    private handleKeyEvent(event: KeyboardEvent) {
        const input = this.el.nativeElement;
        const value = input.value;
        const key = event.key;

        if (this.isNavigationOrModifierKey(event)) {
            return;
        }

        const selectionStart = input.selectionStart ?? 0;
        const selectionEnd = input.selectionEnd ?? 0;
        const nextValue =
            value.substring(0, selectionStart) +
            key +
            value.substring(selectionEnd);

        if (this.isLeadingSpace(selectionStart, key) ||
            this.isMultipleConsecutiveSpaces(key, value, selectionStart) ||
            this.isInvalidSpacePosition(key, selectionStart, value, nextValue) ||
            this.isTooLong(nextValue)) {
            event.preventDefault();
            return;
        }

        if (!this.isValidPerPosition(nextValue)) {
            event.preventDefault();
        }
    }

    private isNavigationOrModifierKey(event: KeyboardEvent): boolean {
        const key = event.key;
        return (
            ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'].includes(key) ||
            event.ctrlKey || event.metaKey
        );
    }

    private isLeadingSpace(selectionStart: number, key: string): boolean {
        return selectionStart === 0 && key === ' ';
    }

    private isMultipleConsecutiveSpaces(key: string, value: string, selectionStart: number): boolean {
        return key === ' ' && value[selectionStart - 1] === ' ';
    }

    private isInvalidSpacePosition(key: string, selectionStart: number, value: string, nextValue: string): boolean {
        if (key !== ' ') return false;
        // Only allow a space after 4 digits, and only if there isn't already a space
        return selectionStart !== 4 || value.includes(' ');
    }

    private isTooLong(nextValue: string): boolean {
        const compact = nextValue.replace(/\s+/g, '');
        return compact.length > 6;
    }

    private isValidPerPosition(nextValue: string): boolean {
        for (let i = 0, j = 0; i < nextValue.length && j < 6; i++) {
            if (nextValue[i] === ' ') continue;
            if (j < 4 && !/\d/.test(nextValue[i])) {
                return false;
            }
            if (j >= 4 && !/[a-zA-Z]/.test(nextValue[i])) {
                return false;
            }
            j++;
        }
        return true;
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        this.handleKeyEvent(event);
    }

    @HostListener('keyup', ['$event'])
    onKeyUp(event: KeyboardEvent) {
        this.updateAndEmitFormattedValue();
    }

    @HostListener('cut', ['$event'])
    onCut(event: ClipboardEvent) {
        setTimeout(() => {
            this.updateAndEmitFormattedValue();
        });
    }

    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent) {
        event.preventDefault();

        const input = this.el.nativeElement;
        const pastedText = (event.clipboardData?.getData('text') ?? '').trim();

        // Get current selection
        const selectionStart = input.selectionStart ?? 0;
        const selectionEnd = input.selectionEnd ?? 0;
        const value = input.value;

        // Insert pasted text at the caret position
        const newValue =
            value.substring(0, selectionStart) +
            pastedText +
            value.substring(selectionEnd);

        const formatted = this.formatPostalCode(newValue);

        // Allow:
        // - 1-4 digits
        // - 4 digits + optional space + 1 or 2 letters
        // Disallow:
        // - 3 digits + space + letter, etc.
        const partialPostalCodePattern = /^([1-9]\d{0,3}|[1-9]\d{3}\s?[a-zA-Z]{0,2})$/;

        if (!partialPostalCodePattern.test(formatted)) {
            return;
        }

        input.value = formatted;
        this.postalCodeController.setValue(formatted);
    }



    private formatPostalCode(value: string): string {
        if (!value) return '';
        // Remove leading/trailing spaces, collapse all internal whitespace to a single space, and uppercase
        let cleaned = value.trim().replace(/\s+/g, ' ').toUpperCase();

        // Remove all spaces for slicing
        const compact = cleaned.replace(/\s/g, '');

        // Only keep first 6 chars
        const sliced = compact.slice(0, 6);

        // Insert a single space after 4 digits if possible
        if (sliced.length >= 5) {
            const digits = sliced.slice(0, 4);
            const letters = sliced.slice(4, 6);
            return `${digits} ${letters}`;
        }
        return sliced;
    }
}
